import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import ButtonBase from '@material-ui/core/ButtonBase';
import { useFirebase } from 'react-redux-firebase';
import { rrfConfig } from 'utils/firebaseService';
import { incrementSignUpCount, processing } from 'containers/Auth/actions';
import { makeSelectSignUpCount } from 'containers/Auth/selectors';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import Mixpanel from 'utils/mixpanelService';

const useStyles = makeStyles({
  root: {
    background: '#ffffff',
    color: '#4B4B4B',
    padding: 12,
    marginBottom: 5,
    fontFamily: 'MadaRegular',
    fontSize: '0.875rem',
    border: '1px solid #4B4B4B',
    '&:hover': {
      opacity: 0.8,
    },
  },
});

function SAMLButton({ isAuthPage = false }) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const firebase = useFirebase();
  const clientDetails = useSelector(makeSelectClientDetails());
  const signUpCount = useSelector(makeSelectSignUpCount());

  const samlConfiguration = _get(clientDetails, 'samlConfiguration', {});
  const shouldDisplay = _get(samlConfiguration, 'enabled', false);
  const label = _get(samlConfiguration, 'label');
  const providerId = _get(samlConfiguration, 'providerId');

  const handleClick = useCallback(() => {
    dispatch(processing(true));
    const provider = new firebase.auth.SAMLAuthProvider(providerId);
    const firestore = firebase.firestore();
    firebase
      .auth()
      .signInWithPopup(provider)
      .then(async userCredential => {
        dispatch(incrementSignUpCount());

        const userData = await firestore
          .collection('users')
          .doc(userCredential.user.uid)
          .get();
        if (!userData.exists) {
          const mappedAdditionalUserInfo = {};
          Object.keys(userCredential.additionalUserInfo.profile).forEach(
            key => {
              mappedAdditionalUserInfo[
                _get(samlConfiguration, ['fieldsMapping', key], key)
              ] = userCredential.additionalUserInfo.profile[key];
            },
          );

          const {
            email,
            firstName,
            lastName,
            ...samlData
          } = mappedAdditionalUserInfo;

          const displayName = userCredential.user.displayName
            ? userCredential.user.displayName
            : firstName && lastName
            ? `${firstName} ${lastName}`
            : firstName || lastName || email;

          const profileData = {
            email: userCredential.user.email || email,
            displayName,
            firstName,
            lastName,
            samlData,
            providerData: userCredential.user.providerData,
            avatarUrl: userCredential.user.photoURL,
          };
          Object.keys(profileData).forEach(
            key => profileData[key] === undefined && delete profileData[key],
          );

          const userProfile = rrfConfig.profileFactory(
            userCredential.user,
            profileData,
          );
          await firestore
            .collection('users')
            .doc(userCredential.user.uid)
            .set(userProfile);

          if (!userCredential.user.email && userProfile.email) {
            await userCredential.user.updateEmail(userProfile.email);
          }
          Mixpanel.track('Sign Up - Successful', { authMethod: 'saml' });
          if (_get(userCredential, 'user.uid'))
            Mixpanel.alias(userCredential.user.uid);
        } else {
          Mixpanel.track('Login - Successful', { authMethod: 'saml' });
          Mixpanel.forceIdentify();
        }
      })
      .catch(error => {
        Mixpanel.track('SAML Authentication - Failed', {
          errorCode: _get(error, 'code', 'undefined'),
        });
      })
      .finally(() => {
        dispatch(processing(false));
      });
  }, [dispatch, firebase, providerId, samlConfiguration]);

  useEffect(() => {
    if (
      isAuthPage &&
      signUpCount === 0 &&
      _get(samlConfiguration, 'enabled', false)
    ) {
      const shouldTriggerSAMLOnLoad = _get(
        samlConfiguration,
        'triggerOnLoad',
        false,
      );
      if (shouldTriggerSAMLOnLoad) {
        handleClick();
      }
    }
  }, [handleClick, isAuthPage, samlConfiguration, signUpCount]);

  if (!shouldDisplay || !providerId) return null;

  return (
    <ButtonBase className={classes.root} onClick={handleClick}>
      {label}
    </ButtonBase>
  );
}

SAMLButton.propTypes = {
  isAuthPage: PropTypes.bool,
};

export default SAMLButton;
